import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { RouteGuardService } from './service/route-guard.service';
import { View } from '../layout/model/interfaces/view.interface';
import { SystemService } from '@shared/service/system/system.service';
import { AuthService } from '../auth/service/auth.service';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { Permissions } from '../auth/model/interfaces/permissions.interface';

@Injectable({ providedIn: 'root' })
export class TemplatesViewGuard implements CanActivate {
  private routeGuardService = inject(RouteGuardService);
  private systemService = inject(SystemService);
  private authService = inject(AuthService);
  private router = inject(Router);

  async canActivate(_route: ActivatedRouteSnapshot): Promise<boolean> {
    // Wait for system to be initialized (permissions loaded)
    await firstValueFrom(
      this.systemService.isInitialized$.pipe(
        filter((val) => val),
        take(1),
      ),
    );

    // Check if user is a superuser - they always have access
    const currentUser = this.authService.user;
    if (currentUser?.isSuperuser) {
      return this.routeGuardService.handleRoute(this.getView());
    }

    // For non-superusers, check if user has permission to view templates
    // Note: Despite the type signature, getCurrentProjectUserPermissions() returns Permissions directly
    const permissions = this.authService.getCurrentProjectUserPermissions() as unknown as Permissions;

    // Use hasProjectTemplateShow permission to differentiate between agents and managers
    // Agents have hasProjectTemplateList but NOT hasProjectTemplateShow
    // Managers have both hasProjectTemplateList AND hasProjectTemplateShow
    if (!permissions?.hasProjectTemplateShow) {
      // User doesn't have permission, redirect to dashboard
      await this.router.navigate(['/dashboard']);
      return false;
    }

    return this.routeGuardService.handleRoute(this.getView());
  }

  getView(): View {
    const projectName = this.systemService.getSelectedProject();
    const iframeUrl = projectName
      ? `templates?project=${projectName}`
      : 'templates';

    return {
      route: '/templates',
      iframeUrl,
    };
  }
}
